import React, { useId } from 'react';

// KIQLogoAnimado (design_handoff_carga) — las 3 piezas reales del logo KIQ
// (barra, diagonal, punto) dentro del marco de marca, animadas de dos
// formas distintas. Geometría, degradados y @keyframes copiados tal cual
// del prototipo (`Pantalla de Carga GastroKIQ.dc.html`, secciones 1a/1c —
// 1b "Rebote" queda solo de referencia, nunca se implementa). Los
// @keyframes (kA_*/kC_*) viven en public/index.html junto a los demás
// (kiqpulse/kiqdot/etc.) — este archivo solo los referencia por nombre.
//
// IDs de degradado/clipPath únicos por instancia vía useId() (sin los ':'
// que useId() incluye — se usan dentro de url(#...), y varias instancias
// pueden convivir en pantalla: p. ej. la pantalla de carga y, en teoría,
// más de un indicador "KIQ pensando").
//
// variante="arma" (1a, PantallaCarga en App.js): el marco se dibuja, la
// barra y la diagonal entran, el punto aparece con rebote — ciclo 2.6s.
// variante="orbita" (1c, indicador "KIQ pensando"): logo estático con un
// anillo de luz recorriendo el marco y un brillo cruzando la K — ciclo 1.8s.
//
// prefers-reduced-motion: reduce — cada pieza animada lleva una clase
// (kiq-logo-arma-piece / kiq-logo-orbita-ring / kiq-logo-orbita-shine) que
// una regla global en public/index.html apaga con !important:
//   - "arma": las 4 piezas quedan opacity:1/transform:none/dashoffset:0 —
//     el logo completo y estático, nunca oculto a medio armar.
//   - "orbita": el anillo pierde su animación y su stroke-dasharray (queda
//     como un brillo parejo de baja opacidad, sin recorrido) y el brillo
//     que cruza la K se oculta del todo — tal cual pide el README ("solo se
//     mantiene el brillo con opacidad baja, sin órbita").
export default function KIQLogoAnimado({ variante = 'arma', size = 132 }) {
  const uid = useId().replace(/:/g, '');
  const idV = `kiqlogo-v-${uid}`;
  const idD = `kiqlogo-d-${uid}`;
  const idT = `kiqlogo-t-${uid}`;
  const idF = `kiqlogo-f-${uid}`;
  const idS = `kiqlogo-s-${uid}`;
  const idK = `kiqlogo-k-${uid}`;
  const esOrbita = variante === 'orbita';

  // Degradado de la diagonal: coordenadas distintas entre variantes porque
  // en "arma" el <rect> vive dentro de un <g transform=...> (coordenadas
  // locales, previas al giro) y en "orbita" el <rect> lleva su propio
  // `transform` (coordenadas ya en el espacio del lienzo completo) — mismo
  // criterio exacto que usa el prototipo en 1a vs. 1c, copiado tal cual.
  const diagonalGradiente = esOrbita
    ? { x1: '240', y1: '280', x2: '370', y2: '140' }
    : { x1: '-33', y1: '0', x2: '189', y2: '0' };

  return (
    <svg width={size} height={size} viewBox="0 0 512 512" fill="none" style={esOrbita ? { overflow: 'visible' } : undefined} role="img" aria-hidden="true">
      <defs>
        <linearGradient id={idV} x1="162" y1="118" x2="162" y2="394" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#00C2FF" />
          <stop offset=".52" stopColor="#176BFF" />
          <stop offset="1" stopColor="#7B31FF" />
        </linearGradient>
        <linearGradient id={idD} x1={diagonalGradiente.x1} y1={diagonalGradiente.y1} x2={diagonalGradiente.x2} y2={diagonalGradiente.y2} gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#7147FF" />
          <stop offset=".52" stopColor="#C63DFF" />
          <stop offset="1" stopColor="#FF4FD8" />
        </linearGradient>
        <linearGradient id={idT} x1="290" y1="385" x2="380" y2="285" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#7B61FF" />
          <stop offset="1" stopColor="#FF4FD8" />
        </linearGradient>
        <linearGradient id={idF} x1="0" y1="0" x2="512" y2="512" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#00C2FF" />
          <stop offset=".5" stopColor="#7B31FF" />
          <stop offset="1" stopColor="#FF4FD8" />
        </linearGradient>
        {esOrbita && (
          <>
            <linearGradient id={idS} x1="0" y1="0" x2="160" y2="0" gradientUnits="userSpaceOnUse">
              <stop offset="0" stopColor="#fff" stopOpacity="0" />
              <stop offset=".5" stopColor="#fff" stopOpacity=".55" />
              <stop offset="1" stopColor="#fff" stopOpacity="0" />
            </linearGradient>
            <clipPath id={idK}>
              <rect x="120" y="118" width="85" height="276" rx="42.5" />
              <rect x="-33" y="-33" width="222" height="66" rx="33" transform="translate(252 262) rotate(-48)" />
              <circle cx="335" cy="335" r="53" />
            </clipPath>
          </>
        )}
      </defs>

      {esOrbita ? (
        <>
          {/* Marco base, apagado — encima corre el anillo de luz. */}
          <rect x="12" y="12" width="488" height="488" rx="84" fill="#0B0B18" stroke="rgba(255,255,255,.08)" strokeWidth="10" />
          <rect
            className="kiq-logo-orbita-ring"
            x="12" y="12" width="488" height="488" rx="84"
            stroke={`url(#${idF})`} strokeWidth="16" strokeLinecap="round"
            pathLength="1580" strokeDasharray="420 1160"
            style={{ animation: 'kC_ring 1.8s linear infinite', filter: 'drop-shadow(0 0 14px rgba(198,61,255,.8))' }}
          />
          <rect x="120" y="118" width="85" height="276" rx="42.5" fill={`url(#${idV})`} />
          <rect x="-33" y="-33" width="222" height="66" rx="33" transform="translate(252 262) rotate(-48)" fill={`url(#${idD})`} />
          <circle cx="335" cy="335" r="53" fill={`url(#${idT})`} />
          <g clipPath={`url(#${idK})`}>
            <rect
              className="kiq-logo-orbita-shine"
              x="0" y="0" width="160" height="512" fill={`url(#${idS})`}
              transform="skewX(-18)"
              style={{ animation: 'kC_shine 1.8s ease-in-out infinite' }}
            />
          </g>
        </>
      ) : (
        <>
          <rect
            className="kiq-logo-arma-piece"
            x="12" y="12" width="488" height="488" rx="84" fill="#0B0B18"
            stroke={`url(#${idF})`} strokeWidth="10" pathLength="1600" strokeDasharray="1600"
            style={{ animation: 'kA_frame 2.6s cubic-bezier(.6,0,.2,1) infinite' }}
          />
          <rect
            className="kiq-logo-arma-piece"
            x="120" y="118" width="85" height="276" rx="42.5" fill={`url(#${idV})`}
            style={{ transformBox: 'fill-box', transformOrigin: '50% 100%', animation: 'kA_bar 2.6s cubic-bezier(.3,1.3,.5,1) infinite' }}
          />
          <g transform="translate(252 262) rotate(-48)">
            <rect
              className="kiq-logo-arma-piece"
              x="-33" y="-33" width="222" height="66" rx="33" fill={`url(#${idD})`}
              style={{ transformBox: 'fill-box', transformOrigin: '0 50%', animation: 'kA_slash 2.6s cubic-bezier(.3,1.25,.5,1) infinite' }}
            />
          </g>
          <circle
            className="kiq-logo-arma-piece"
            cx="335" cy="335" r="53" fill={`url(#${idT})`}
            style={{ transformBox: 'fill-box', transformOrigin: '50% 50%', animation: 'kA_dot 2.6s ease-out infinite' }}
          />
        </>
      )}
    </svg>
  );
}
